import React, { Fragment, memo, useCallback, useEffect, useState } from 'react'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import NoFillIcon from '@material-ui/icons/NotInterestedOutlined'
import Button from '@material-ui/core/Button'
import DeleteIcon from '@material-ui/icons/DeleteOutlineOutlined'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Divider from '@material-ui/core/Divider'
import ExpandMoreIcon from '@material-ui/icons/ExpandMoreOutlined'
import IconButton from '@material-ui/core/IconButton'
import Input from '@material-ui/core/Input'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import ListItemText from '@material-ui/core/ListItemText'
import Checkbox from '@material-ui/core/Checkbox'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { ConfirmDelete } from '../confirmation/Confirm'
import _ from 'lodash'
import LOG from '../../logging/Logger'
import { MAX_LENGTH_AREA_NAME } from '../../data/limits'
import { borderColor } from '@material-ui/system'
import ColourPicker from '../ColourPicker'
import { useMountEffect } from '../../data/hooks'

const useStyles = makeStyles(theme => ({
  root: {
    width: 'fit-content'
  },
  textField: {
    marginTop: theme.spacing(2),
    minWidth: 400,
    maxWidth: 600,
    [theme.breakpoints.down('xs')]: {
      minWidth: '100%',
      maxWidth: '100%'
    }
  },
  listItem: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 4,
    paddingBottom: 4
  },
  itemText: {
    overflowWrap: 'anywhere',
    display: 'inline-block',
    wordBreak: 'break-word'
  },
  navigationText: {
    cursor: 'pointer',
    flexGrow: 1
  },
  areaText: {
    cursor: 'pointer',
    verticalAlign: 'middle',
    textOverflow: 'ellipsis'
  },
  content: {
    textAlign: 'center',
    maxWidth: 'calc(100% - 140px)'
  },
  colourPickerBox: {
    width: '30px',
    height: '30px',
    margin: '3px',
    position: 'relative',
    border: '1px solid',
    float: 'left'
  }
}))

export default memo(
  ({
    getAreaFilter,
    updateAreaFilter,
    getAreas,
    createArea,
    deleteArea,
    editArea,
    decrypt,
    navigationSelectedItem,
    setOpenAreaFilter,
    openAreaFilter
  }) => {
    LOG.debug('Render: AreaFilter')

    const classes = useStyles()
    const theme = useTheme()

    const [availableAreas, setAvailableAreas] = useState([])
    const [checked, setChecked] = useState([0])
    const [newArea, setNewArea] = useState('')
    const [openConfirmDeleteArea, setOpenConfirmDeleteArea] = useState(false)

    const [openHightlightArea, setOpenHightlightArea] = useState(null)

    const fullScreen = useMediaQuery(theme.breakpoints.down('xs'), {
      noSsr: true
    })

    useEffect(() => {
      setAvailableAreas(getAreas())
    }, [getAreas])

    useMountEffect(() => {
      const newChecked = []
      _.each(getAreaFilter(), area => {
        newChecked.push(area.id)
      })
      setChecked(newChecked)
    })

    const handleToggle = useCallback(
      value => () => {
        const currentIndex = checked.indexOf(value.id)
        const newChecked = [...checked]

        if (currentIndex === -1) {
          newChecked.push(value.id)
        } else {
          newChecked.splice(currentIndex, 1)
        }

        setChecked(newChecked)
      },
      [checked]
    )

    const handleClickOpen = useCallback(() => {
      setOpenAreaFilter(true)
    }, [setOpenAreaFilter])

    const handleClose = useCallback(
      save => {
        setOpenAreaFilter(false)
        if (save) {
          updateAreaFilter(
            availableAreas.filter(area => {
              return checked.findIndex(id => id === area.id) !== -1
            })
          )
        }
      },
      [availableAreas, checked, updateAreaFilter, setOpenAreaFilter]
    )

    const handleKeyPress = useCallback(
      event => {
        if (event.repeat) {
          return
        }
        if (event.keyCode === 13) {
          if (createArea(newArea.trim())) {
            setNewArea('')
          }
        }
      },
      [createArea, newArea]
    )

    const handleSelectHighlightColour = useCallback(
      async colour => {
        if (openHightlightArea) {
          await editArea(
            openHightlightArea.id,
            decrypt(openHightlightArea.name),
            colour ? colour.hex : null
          )
          setOpenHightlightArea(null)
        }
      },
      [decrypt, editArea, openHightlightArea]
    )

    const isAllItems = useCallback(() => {
      const currentAreaFilter = getAreaFilter()
      return (
        !currentAreaFilter ||
        currentAreaFilter.length === 0 ||
        (availableAreas.length > 1 &&
          currentAreaFilter.length === availableAreas.length)
      )
    }, [availableAreas.length, getAreaFilter])

    const getAreasString = useCallback(() => {
      if (isAllItems()) {
        return 'All Areas'
      } else {
        const currentAreaFilter = getAreaFilter()
        return currentAreaFilter.map(
          (area, index) =>
            decrypt(area.name) +
            (index < currentAreaFilter.length - 1 ? ', ' : '')
        )
      }
    }, [decrypt, getAreaFilter, isAllItems])

    return (
      <Fragment>
        <div className={classes.content}>
          <Typography
            color={'inherit'}
            className={classes.navigationText}
            noWrap={true}
            onClick={handleClickOpen}
            variant='h6'
          >
            {navigationSelectedItem.title}
          </Typography>
          <Typography
            className={classes.areaText}
            noWrap={true}
            onClick={handleClickOpen}
            variant='subtitle1'
            color={isAllItems() ? 'textSecondary' : 'secondary'}
          >
            {getAreasString()}
            <ExpandMoreIcon />
          </Typography>
        </div>
        <Dialog
          fullScreen={fullScreen}
          fullWidth={true}
          open={openAreaFilter}
          onClose={() => handleClose(false)}
        >
          <DialogTitle>Select Areas</DialogTitle>
          <DialogContent>
            <Input
              id='add-area'
              name='Add Area'
              fullWidth={true}
              className={classes.textField}
              placeholder='Add Area...'
              variant='outlined'
              value={newArea}
              onChange={e => setNewArea(e.target.value)}
              onKeyDown={e => handleKeyPress(e)}
              inputProps={{
                'aria-label': 'add-area',
                'data-lpignore': 'true',
                maxLength: MAX_LENGTH_AREA_NAME
              }}
            />
            <List>
              {availableAreas.map((value, index) => {
                return (
                  <ListItem
                    className={classes.listItem}
                    key={value.id}
                    button
                    onClick={handleToggle(value)}
                    ContainerProps={{
                      style: { display: 'flex', alignItems: 'center' }
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge='start'
                        checked={checked.indexOf(value.id) !== -1}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      className={classes.itemText}
                      primary={decrypt(value.name)}
                    />
                    <ListItemSecondaryAction
                      style={{ position: 'static', transform: 'none' }}
                    >
                      <div
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden'
                        }}
                      >
                        {index > 0 && (
                          <Tooltip
                            title={`Set '${decrypt(
                              value.name
                            )}' Highlight Color`}
                          >
                            <IconButton
                              onClick={() => setOpenHightlightArea(value)}
                            >
                              <div
                                className={classes.colourPickerBox}
                                style={{
                                  borderColor: borderColor,
                                  backgroundColor: value.colour,
                                  textAlign: 'center'
                                }}
                              >
                                {!value.colour && (
                                  <NoFillIcon
                                    style={{ paddingTop: 2 }}
                                    color='secondary'
                                  />
                                )}
                              </div>
                            </IconButton>
                          </Tooltip>
                        )}
                        {index > 0 && (
                          <IconButton
                            className={classes.delete}
                            color='secondary'
                            onClick={e => setOpenConfirmDeleteArea(value)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </div>
                    </ListItemSecondaryAction>
                  </ListItem>
                )
              })}
            </List>
            <br />
            <Divider />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setChecked([0])}
              disabled={_.isEqual(checked, [0])}
              color='secondary'
            >
              Reset
            </Button>
            <Button onClick={handleClose} color='primary'>
              Ok
            </Button>
          </DialogActions>
        </Dialog>
        <ConfirmDelete
          open={openConfirmDeleteArea}
          deleteItem={decrypt(openConfirmDeleteArea.name)}
          onOk={useCallback(async () => {
            await deleteArea(openConfirmDeleteArea)
            setOpenConfirmDeleteArea(false)
          }, [deleteArea, openConfirmDeleteArea])}
          onCancel={useCallback(() => setOpenConfirmDeleteArea(false), [])}
        />
        <ColourPicker
          open={openHightlightArea !== null}
          onClose={() => setOpenHightlightArea(null)}
          onSave={handleSelectHighlightColour}
        />
      </Fragment>
    )
  }
)
